/**
 * Fuel.jsx
 *
 * Fuel Depot - Strategic fuel trading system.
 * Three tabs: Spot Buy, Contracts, Hedging
 * Plus price history chart and active agreements.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from '../components/state/WorldStateContext.jsx';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FuelGauge } from '../components/fuel/FuelGauge.jsx';
import { PriceHistoryChart } from '../components/fuel/PriceHistoryChart.jsx';
import {
  getTrendDisplayInfo,
  CONTRACT_OPTIONS,
  canSignContract,
  signFuelContract,
  getEffectivePurchasePrice,
  recordContractPurchase,
  HEDGE_OPTIONS,
  canPurchaseHedge,
  purchaseFuelHedge,
} from '../systems/FuelSystem.js';

// Quick purchase amounts for spot buying
const PURCHASE_OPTIONS = [
  { gallons: 50000, label: '50K gal' },
  { gallons: 100000, label: '100K gal' },
  { gallons: 250000, label: '250K gal' },
  { gallons: 500000, label: '500K gal' },
  { gallons: 1000000, label: '1M gal' },
];

// Tab options
const TABS = [
  { id: 'spot', label: 'SPOT BUY' },
  { id: 'contracts', label: 'CONTRACTS' },
  { id: 'hedging', label: 'HEDGING' },
];

function formatMoney(amount) {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toLocaleString()}`;
}

function formatGallons(gal) {
  if (gal >= 1000000) return `${(gal / 1000000).toFixed(1)}M`;
  if (gal >= 1000) return `${Math.round(gal / 1000)}K`;
  return gal.toLocaleString();
}

export default function FuelPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { worldState, setWorldState } = useWorldState();
  const [activeTab, setActiveTab] = useState('spot');
  const [customAmount, setCustomAmount] = useState('');
  const [purchaseMessage, setPurchaseMessage] = useState(null);

  // Tab definitions with i18n
  const TABS_I18N = [
    { id: 'spot', label: t('fuel.tabs.spot', 'SPOT BUY') },
    { id: 'contracts', label: t('fuel.tabs.contracts', 'CONTRACTS') },
    { id: 'hedging', label: t('fuel.tabs.hedging', 'HEDGING') },
  ];

  // Get fuel data
  const fuelReserves = worldState?.fuelReserves || 500000;
  const maxFuel = worldState?.maxFuelCapacity || 2000000;
  const baseSpotPrice = worldState?.fuelSpotPrice || 3.00;
  const priceHistory = worldState?.fuelPriceHistory || [];
  const cash = worldState?.airline?.cash || 0;
  const trend = worldState?.fuelMarketTrend;
  const trendInfo = getTrendDisplayInfo(trend);
  const dayNumber = worldState?.dayNumber || 1;

  // Check for Market Maker fuel chaos
  const chaos = worldState?.fuelMarketChaos;
  const isChaosActive = chaos?.active && dayNumber <= chaos?.endDay;

  // During chaos, show fluctuating price within chaos range
  const [chaosPrice, setChaosPrice] = React.useState(baseSpotPrice);

  React.useEffect(() => {
    if (!isChaosActive) {
      setChaosPrice(baseSpotPrice);
      return;
    }

    // Fluctuate price every 500ms during chaos
    const interval = setInterval(() => {
      const newPrice = chaos.priceMin + (Math.random() * (chaos.priceMax - chaos.priceMin));
      setChaosPrice(newPrice);
    }, 500);

    return () => clearInterval(interval);
  }, [isChaosActive, chaos?.priceMin, chaos?.priceMax, baseSpotPrice]);

  const spotPrice = isChaosActive ? chaosPrice : baseSpotPrice;

  // Active contracts and hedges
  const fuelContract = worldState?.fuelContract;
  const fuelHedge = worldState?.fuelHedge;

  // Get effective price (considers contract/hedge)
  const effectivePrice = getEffectivePurchasePrice(worldState);
  const hasActiveContract = fuelContract?.status === 'active' && dayNumber <= fuelContract?.endDay;
  const canSign = canSignContract(worldState);
  const hasActiveHedge = fuelHedge?.status === 'active' && dayNumber <= fuelHedge?.endDay;
  const canBuyHedge = canPurchaseHedge(worldState);

  // Calculate fill percentage
  const fillPercent = Math.round((fuelReserves / maxFuel) * 100);

  // Estimate days of fuel remaining
  const estimatedDays = useMemo(() => {
    const dailyBurn = 50000; // Rough estimate
    return Math.floor(fuelReserves / dailyBurn);
  }, [fuelReserves]);

  // Calculate max affordable (using effective price)
  const maxAffordable = useMemo(() => {
    const maxByMoney = Math.floor(cash / effectivePrice);
    const maxByCapacity = maxFuel - fuelReserves;
    return Math.min(maxByMoney, maxByCapacity);
  }, [cash, effectivePrice, maxFuel, fuelReserves]);

  // Handle fuel purchase
  const handlePurchase = (gallons) => {
    if (gallons <= 0) return;

    const cost = gallons * effectivePrice;

    if (cost > cash) {
      setPurchaseMessage({ type: 'error', text: t('fuel.spot.insufficientFunds', { amount: formatMoney(cost) }) });
      return;
    }

    const newFuel = fuelReserves + gallons;
    if (newFuel > maxFuel) {
      setPurchaseMessage({ type: 'error', text: t('fuel.spot.exceedCapacity', { amount: formatGallons(maxFuel) }) });
      return;
    }

    setWorldState(prev => {
      // Record against contract if active
      const updatedContract = hasActiveContract
        ? recordContractPurchase(prev.fuelContract, gallons)
        : prev.fuelContract;

      return {
        ...prev,
        fuelReserves: (prev.fuelReserves || 500000) + gallons,
        fuelContract: updatedContract,
        airline: {
          ...prev.airline,
          cash: (prev.airline?.cash || 0) - cost,
        },
      };
    });

    const priceNote = hasActiveContract ? ` ${t('fuel.spot.contractPriceNote')}` : '';
    setPurchaseMessage({
      type: 'success',
      text: `${t('fuel.spot.purchaseSuccess', { amount: formatGallons(gallons), cost: formatMoney(cost) })}${priceNote}`,
    });

    setTimeout(() => setPurchaseMessage(null), 3000);
  };

  // Handle signing a contract
  const handleSignContract = (days) => {
    const result = signFuelContract(worldState, days);
    if (!result.success) {
      setPurchaseMessage({ type: 'error', text: result.error });
      return;
    }

    setWorldState(prev => ({
      ...prev,
      fuelContract: result.newContract,
    }));

    setPurchaseMessage({
      type: 'success',
      text: t('fuel.contracts.signedSuccess', { days, price: result.newContract.lockedPrice.toFixed(2) }),
    });
    setTimeout(() => setPurchaseMessage(null), 3000);
  };

  // Handle purchasing a hedge
  const handlePurchaseHedge = (days) => {
    const result = purchaseFuelHedge(worldState, days);
    if (!result.success) {
      setPurchaseMessage({ type: 'error', text: result.error });
      return;
    }

    setWorldState(prev => ({
      ...prev,
      fuelHedge: result.newHedge,
      airline: {
        ...prev.airline,
        cash: (prev.airline?.cash || 0) - result.cost,
      },
    }));

    setPurchaseMessage({
      type: 'success',
      text: t('fuel.hedging.purchasedSuccess', { days, price: result.newHedge.capPrice.toFixed(2) }),
    });
    setTimeout(() => setPurchaseMessage(null), 3000);
  };

  const handleCustomPurchase = () => {
    const gallons = parseInt(customAmount.replace(/,/g, ''), 10);
    if (isNaN(gallons) || gallons <= 0) {
      setPurchaseMessage({ type: 'error', text: t('fuel.spot.enterValidAmount') });
      return;
    }
    handlePurchase(gallons);
    setCustomAmount('');
  };

  const handleFillTank = () => {
    handlePurchase(maxAffordable);
  };

  // Render Spot Buy tab content
  const renderSpotBuy = () => (
    <div>
      {/* Contract Active Banner */}
      {hasActiveContract && (
        <div
          className="mb-4 p-3 rounded-lg flex items-center justify-between"
          style={{ backgroundColor: '#0d2818', border: '1px solid #22c55e50' }}
        >
          <div>
            <span className="text-sm font-bold" style={{ color: '#22c55e' }}>{t('fuel.spot.contractActive')}</span>
            <span className="text-xs ml-2" style={{ color: '#737373' }}>
              {t('fuel.spot.buyingAt', { effectivePrice: effectivePrice.toFixed(2), spotPrice: spotPrice.toFixed(2) })}
            </span>
          </div>
          <div className="text-xs font-mono" style={{ color: '#22c55e' }}>
            {t('fuel.spot.saving', { amount: (spotPrice - effectivePrice).toFixed(2) })}
          </div>
        </div>
      )}

      {/* Message */}
      {purchaseMessage && (
        <div
          className="mb-4 p-3 rounded"
          style={{
            backgroundColor: purchaseMessage.type === 'success' ? '#0d2818' : '#1a0a0a',
            border: `1px solid ${purchaseMessage.type === 'success' ? '#22c55e' : '#ef4444'}`,
            color: purchaseMessage.type === 'success' ? '#22c55e' : '#ef4444',
          }}
        >
          {purchaseMessage.text}
        </div>
      )}

      {/* Quick Purchase Buttons */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {PURCHASE_OPTIONS.map((option) => {
          const cost = option.gallons * effectivePrice;
          const canAfford = cost <= cash;
          const wouldOverflow = fuelReserves + option.gallons > maxFuel;
          const disabled = !canAfford || wouldOverflow;

          return (
            <button
              key={option.gallons}
              onClick={() => handlePurchase(option.gallons)}
              disabled={disabled}
              style={{
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: disabled ? '#1a1a1a' : '#0d1f1a',
                border: `1px solid ${disabled ? '#333' : '#22c55e50'}`,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <div className="text-base font-bold" style={{ color: disabled ? '#525252' : '#22c55e' }}>
                {option.label}
              </div>
              <div className="text-xs font-mono" style={{ color: disabled ? '#404040' : '#737373' }}>
                {formatMoney(cost)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom Amount */}
      <div className="flex gap-3 mb-4">
        <input
          type="text"
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          placeholder={t('fuel.spot.customPlaceholder')}
          style={{
            flex: 1,
            padding: '10px 14px',
            backgroundColor: '#111',
            border: '1px solid #333',
            borderRadius: '6px',
            color: '#e5e5e5',
            fontSize: '14px',
            fontFamily: 'monospace',
          }}
        />
        <Button
          onClick={handleCustomPurchase}
          disabled={!customAmount}
          style={{
            backgroundColor: '#f97316',
            color: '#000',
            fontWeight: '700',
          }}
        >
          {t('fuel.spot.buy')}
        </Button>
      </div>

      {/* Fill Tank Button */}
      <Button
        onClick={handleFillTank}
        disabled={maxAffordable <= 0}
        className="w-full"
        style={{
          backgroundColor: maxAffordable > 0 ? '#22c55e' : '#333',
          color: maxAffordable > 0 ? '#000' : '#666',
          fontWeight: '700',
          padding: '14px',
          fontSize: '14px',
        }}
      >
        {maxAffordable > 0
          ? t('fuel.spot.fillTank', { amount: formatGallons(maxAffordable), cost: formatMoney(maxAffordable * effectivePrice) })
          : t('fuel.spot.tankFullOrNoFunds')}
      </Button>
    </div>
  );

  // Render Contracts tab content
  const renderContracts = () => (
    <div>
      {/* Message */}
      {purchaseMessage && (
        <div
          className="mb-4 p-3 rounded"
          style={{
            backgroundColor: purchaseMessage.type === 'success' ? '#0d2818' : '#1a0a0a',
            border: `1px solid ${purchaseMessage.type === 'success' ? '#22c55e' : '#ef4444'}`,
            color: purchaseMessage.type === 'success' ? '#22c55e' : '#ef4444',
          }}
        >
          {purchaseMessage.text}
        </div>
      )}

      {/* Active Contract Info */}
      {hasActiveContract && (
        <div
          className="mb-4 p-4 rounded-lg"
          style={{ backgroundColor: '#0d2818', border: '1px solid #22c55e50' }}
        >
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-bold" style={{ color: '#22c55e' }}>
                {t('fuel.contracts.activeContract')}
              </div>
              <div className="text-xs" style={{ color: '#737373' }}>
                {t('fuel.contracts.activeDesc', { price: fuelContract.lockedPrice.toFixed(2) })}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono font-bold" style={{ color: '#22c55e' }}>
                {t('fuel.contracts.daysLeft', { count: fuelContract.endDay - dayNumber })}
              </div>
              <div className="text-xs" style={{ color: '#525252' }}>
                {t('fuel.contracts.commitmentRemaining', { amount: formatGallons(fuelContract.minimumGallons - fuelContract.purchasedGallons) })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Options */}
      <div className="text-sm font-bold mb-3" style={{ color: '#f97316' }}>
        {hasActiveContract ? t('fuel.contracts.optionsReplace') : t('fuel.contracts.optionsAvailable')}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {CONTRACT_OPTIONS.map((contract) => {
          const lockedPrice = spotPrice * (1 - contract.discount);
          const savings = (spotPrice - lockedPrice) * contract.minimumGallons;
          const canAffordCommitment = cash >= lockedPrice * contract.minimumGallons * 0.1; // 10% minimum immediate capacity

          return (
            <div
              key={contract.days}
              className="p-4 rounded-lg"
              style={{
                backgroundColor: '#111',
                border: '1px solid #333',
              }}
            >
              <div className="text-center mb-3">
                <div className="text-2xl font-bold" style={{ color: '#60ffd0' }}>
                  {contract.label}
                </div>
                <div className="text-xs" style={{ color: '#525252' }}>
                  {t('fuel.contracts.dayPriceLock', { count: contract.days })}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.contracts.lockedPrice')}</span>
                  <span className="font-mono font-bold" style={{ color: '#22c55e' }}>
                    ${lockedPrice.toFixed(2)}/gal
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.contracts.discount')}</span>
                  <span className="font-mono" style={{ color: '#60ffd0' }}>
                    {t('fuel.contracts.offSpot', { percent: (contract.discount * 100).toFixed(0) })}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.contracts.minPurchase')}</span>
                  <span className="font-mono" style={{ color: '#f97316' }}>
                    {formatGallons(contract.minimumGallons)} {t('fuel.gal')}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.contracts.estSavings')}</span>
                  <span className="font-mono" style={{ color: '#22c55e' }}>
                    {formatMoney(savings)}
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleSignContract(contract.days)}
                disabled={!canSign || !canAffordCommitment}
                className="w-full py-2 rounded font-bold text-sm transition-all"
                style={{
                  backgroundColor: canSign && canAffordCommitment ? '#22c55e' : '#333',
                  color: canSign && canAffordCommitment ? '#000' : '#666',
                  cursor: canSign && canAffordCommitment ? 'pointer' : 'not-allowed',
                }}
              >
                {hasActiveContract ? t('fuel.contracts.replaceContract') : t('fuel.contracts.signContract')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Contract Info */}
      <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
        <div className="text-xs" style={{ color: '#525252' }}>
          <strong style={{ color: '#737373' }}>{t('fuel.contracts.howItWorks')}</strong> {t('fuel.contracts.howItWorksDesc')}
        </div>
      </div>
    </div>
  );

  // Render Hedging tab content
  const renderHedging = () => (
    <div>
      {/* Message */}
      {purchaseMessage && (
        <div
          className="mb-4 p-3 rounded"
          style={{
            backgroundColor: purchaseMessage.type === 'success' ? '#0d2818' : '#1a0a0a',
            border: `1px solid ${purchaseMessage.type === 'success' ? '#22c55e' : '#ef4444'}`,
            color: purchaseMessage.type === 'success' ? '#22c55e' : '#ef4444',
          }}
        >
          {purchaseMessage.text}
        </div>
      )}

      {/* Active Hedge Info */}
      {hasActiveHedge && (
        <div
          className="mb-4 p-4 rounded-lg"
          style={{ backgroundColor: '#0d1f2a', border: '1px solid #60ffd050' }}
        >
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-bold" style={{ color: '#60ffd0' }}>
                {t('fuel.hedging.activeHedge')}
              </div>
              <div className="text-xs" style={{ color: '#737373' }}>
                {t('fuel.hedging.priceCap', { price: fuelHedge.capPrice.toFixed(2) })}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono font-bold" style={{ color: '#60ffd0' }}>
                {t('fuel.hedging.daysLeft', { count: fuelHedge.endDay - dayNumber })}
              </div>
              <div className="text-xs" style={{ color: '#525252' }}>
                {t('fuel.hedging.premiumPaid', { amount: formatMoney(fuelHedge.premiumPaid) })}
              </div>
            </div>
          </div>
          {spotPrice > fuelHedge.capPrice && (
            <div className="mt-2 text-xs font-bold" style={{ color: '#22c55e' }}>
              {t('fuel.hedging.hedgeSaving', { amount: (spotPrice - fuelHedge.capPrice).toFixed(2) })}
            </div>
          )}
        </div>
      )}

      {/* Hedge Options */}
      <div className="text-sm font-bold mb-3" style={{ color: '#f97316' }}>
        {hasActiveHedge ? t('fuel.hedging.optionsReplace') : t('fuel.hedging.optionsAvailable')}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {HEDGE_OPTIONS.map((hedge) => {
          const capPrice = spotPrice * 1.10;
          const canAfford = cash >= hedge.premium;

          return (
            <div
              key={hedge.days}
              className="p-4 rounded-lg"
              style={{
                backgroundColor: '#111',
                border: '1px solid #333',
              }}
            >
              <div className="text-center mb-3">
                <div className="text-2xl font-bold" style={{ color: '#60ffd0' }}>
                  {hedge.label}
                </div>
                <div className="text-xs" style={{ color: '#525252' }}>
                  {t('fuel.hedging.dayProtection', { count: hedge.days })}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.hedging.priceCapLabel')}</span>
                  <span className="font-mono font-bold" style={{ color: '#60ffd0' }}>
                    ${capPrice.toFixed(2)}/gal
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.hedging.capFormula')}</span>
                  <span className="font-mono" style={{ color: '#525252' }}>
                    {t('fuel.hedging.spotPlus10')}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.hedging.premium')}</span>
                  <span className="font-mono font-bold" style={{ color: '#f97316' }}>
                    {formatMoney(hedge.premium)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#737373' }}>{t('fuel.hedging.breakEven')}</span>
                  <span className="font-mono" style={{ color: '#737373' }}>
                    {t('fuel.hedging.spotGreaterThan', { price: capPrice.toFixed(2) })}
                  </span>
                </div>
              </div>

              <button
                onClick={() => handlePurchaseHedge(hedge.days)}
                disabled={!canBuyHedge || !canAfford}
                className="w-full py-2 rounded font-bold text-sm transition-all"
                style={{
                  backgroundColor: canBuyHedge && canAfford ? '#60ffd0' : '#333',
                  color: canBuyHedge && canAfford ? '#000' : '#666',
                  cursor: canBuyHedge && canAfford ? 'pointer' : 'not-allowed',
                }}
              >
                {!canAfford ? t('fuel.hedging.cannotAfford') : hasActiveHedge ? t('fuel.hedging.replaceHedge') : t('fuel.hedging.purchaseHedge')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Hedge Info */}
      <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
        <div className="text-xs" style={{ color: '#525252' }}>
          <strong style={{ color: '#737373' }}>{t('fuel.hedging.howItWorks')}</strong> {t('fuel.hedging.howItWorksDesc')}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a', color: '#e5e5e5' }}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-3 border-b"
        style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/opscenter')}
            style={{ color: '#737373' }}
          >
            {t('fuel.backToOps')}
          </Button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#f97316' }}>
              {t('fuel.title')}
            </h1>
            <p className="text-xs" style={{ color: '#737373' }}>
              {t('fuel.subtitle')}
            </p>
          </div>
        </div>

        {/* Current Cash & Price */}
        <div className="flex items-center gap-6">
          {/* Effective Price (when contract active) */}
          {hasActiveContract && (
            <div className="text-right">
              <div className="text-xs uppercase" style={{ color: '#737373' }}>
                {t('fuel.yourPrice')}
              </div>
              <div className="text-xl font-bold font-mono" style={{ color: '#22c55e' }}>
                ${effectivePrice.toFixed(2)}/gal
              </div>
              <div className="text-xs" style={{ color: '#525252' }}>
                {t('fuel.contractLocked')}
              </div>
            </div>
          )}

          {/* Spot Price */}
          <div className="text-right">
            <div className="text-xs uppercase flex items-center justify-end gap-2" style={{ color: '#737373' }}>
              {isChaosActive && (
                <span style={{
                  color: '#ec4899',
                  fontWeight: 'bold',
                  animation: 'pulse 0.5s ease-in-out infinite',
                }}>
                  {t('fuel.chaos')}
                </span>
              )}
              {hasActiveContract ? t('fuel.market') : t('fuel.spotPrice')}
            </div>
            <div
              className={`font-bold font-mono ${hasActiveContract ? 'text-base' : 'text-xl'}`}
              style={{
                color: isChaosActive ? '#ec4899' : (hasActiveContract ? '#737373' : '#22c55e'),
                animation: isChaosActive ? 'pulse 0.3s ease-in-out infinite' : 'none',
              }}
            >
              ${spotPrice.toFixed(2)}/gal
            </div>
            <div className="text-xs flex items-center justify-end gap-1" style={{ color: isChaosActive ? '#ec4899' : trendInfo.color }}>
              {isChaosActive ? (
                <>
                  <span>{t('fuel.marketChaos')}</span>
                  <span style={{ color: '#525252' }}>({chaos.endDay - dayNumber}d)</span>
                </>
              ) : (
                <>
                  <span>{trendInfo.icon}</span>
                  <span>{trendInfo.label}</span>
                  {trend?.daysRemaining && (
                    <span style={{ color: '#525252' }}>({trend.daysRemaining}d)</span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Cash */}
          <div className="text-right">
            <div className="text-xs uppercase" style={{ color: '#737373' }}>
              {t('fuel.availableCash')}
            </div>
            <div className="text-xl font-bold font-mono" style={{ color: '#22c55e' }}>
              {formatMoney(cash)}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {/* Top Row: Tank + Chart */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          {/* Fuel Tank Status */}
          <div
            className="col-span-1 p-4 rounded-lg"
            style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}
          >
            <div className="text-sm font-bold mb-3" style={{ color: '#f97316' }}>
              {t('fuel.tankStatus')}
            </div>
            <div className="flex justify-center mb-3">
              <div style={{ width: '100px' }}>
                <FuelGauge
                  currentFuel={fuelReserves}
                  maxFuel={maxFuel}
                  dailyBurnRate={50000}
                  onClick={() => {}}
                />
              </div>
            </div>
            <div className="space-y-1 text-center text-xs">
              <div>
                <span style={{ color: '#525252' }}>{t('fuel.current')} </span>
                <span className="font-mono font-bold" style={{ color: '#f97316' }}>
                  {formatGallons(fuelReserves)} {t('fuel.gal')}
                </span>
              </div>
              <div>
                <span style={{ color: '#525252' }}>{t('fuel.capacity')} </span>
                <span className="font-mono" style={{ color: '#737373' }}>
                  {formatGallons(maxFuel)} {t('fuel.gal')}
                </span>
              </div>
              <div>
                <span style={{ color: '#525252' }}>{t('fuel.fill')} </span>
                <span className="font-mono font-bold" style={{
                  color: fillPercent < 25 ? '#ef4444' : fillPercent < 50 ? '#f97316' : '#22c55e'
                }}>
                  {fillPercent}%
                </span>
              </div>
              <div>
                <span style={{ color: '#525252' }}>~</span>
                <span className="font-mono" style={{ color: '#737373' }}>
                  {estimatedDays} {t('fuel.days')}
                </span>
              </div>
            </div>
          </div>

          {/* Price History Chart */}
          <div className="col-span-3">
            <PriceHistoryChart
              priceHistory={priceHistory}
              currentPrice={spotPrice}
              width={600}
              height={180}
            />
          </div>
        </div>

        {/* Tab Bar */}
        <div
          className="flex gap-1 p-1 rounded-lg mb-4"
          style={{ backgroundColor: '#111' }}
        >
          {TABS_I18N.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 py-2 px-4 rounded text-sm font-bold transition-all"
              style={{
                backgroundColor: activeTab === tab.id ? '#1a1a1a' : 'transparent',
                color: activeTab === tab.id ? '#f97316' : '#525252',
                border: activeTab === tab.id ? '1px solid #333' : '1px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          className="p-4 rounded-lg mb-4"
          style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}
        >
          {activeTab === 'spot' && renderSpotBuy()}
          {activeTab === 'contracts' && renderContracts()}
          {activeTab === 'hedging' && renderHedging()}
        </div>

        {/* Active Agreements Section */}
        <div
          className="p-4 rounded-lg"
          style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}
        >
          <div className="text-sm font-bold mb-3" style={{ color: '#f97316' }}>
            {t('fuel.agreements.title')}
          </div>

          {!fuelContract && !fuelHedge ? (
            <div className="text-center py-4 text-sm" style={{ color: '#525252' }}>
              {t('fuel.agreements.noAgreements')}
            </div>
          ) : (
            <div className="space-y-2">
              {fuelContract && fuelContract.status === 'active' && (
                <div
                  className="p-3 rounded"
                  style={{ backgroundColor: '#111', border: '1px solid #22c55e30' }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-sm" style={{ color: '#22c55e' }}>
                        {t('fuel.agreements.dayContract', { count: fuelContract.endDay - fuelContract.startDay })}
                      </div>
                      <div className="text-xs" style={{ color: '#737373' }}>
                        {t('fuel.agreements.lockedPrice', { price: fuelContract.lockedPrice.toFixed(2) })}
                      </div>
                    </div>
                    <div className="text-right text-xs" style={{ color: '#525252' }}>
                      <div>{t('fuel.agreements.expiresDay', { day: fuelContract.endDay })}</div>
                      <div>{t('fuel.agreements.daysRemaining', { count: fuelContract.endDay - dayNumber })}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs" style={{ color: '#525252' }}>
                    {t('fuel.agreements.minPurchased', {
                      min: formatGallons(fuelContract.minimumGallons),
                      purchased: formatGallons(fuelContract.purchasedGallons),
                      remaining: formatGallons(fuelContract.minimumGallons - fuelContract.purchasedGallons)
                    })}
                  </div>
                </div>
              )}

              {fuelHedge && fuelHedge.status === 'active' && (
                <div
                  className="p-3 rounded"
                  style={{ backgroundColor: '#111', border: '1px solid #60ffd030' }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-sm" style={{ color: '#60ffd0' }}>
                        {t('fuel.agreements.dayHedge', { count: fuelHedge.endDay - fuelHedge.startDay })}
                      </div>
                      <div className="text-xs" style={{ color: '#737373' }}>
                        {t('fuel.agreements.priceCap', { price: fuelHedge.capPrice.toFixed(2) })}
                      </div>
                    </div>
                    <div className="text-right text-xs" style={{ color: '#525252' }}>
                      <div>{t('fuel.agreements.expiresDay', { day: fuelHedge.endDay })}</div>
                      <div>{t('fuel.agreements.daysRemaining', { count: fuelHedge.endDay - dayNumber })}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs" style={{ color: '#525252' }}>
                    {t('fuel.agreements.premiumStatus', { premium: formatMoney(fuelHedge.premiumPaid), status: fuelHedge.status })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
